import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useExpenseAuditLogs } from '@/api/audit-logs'
import { useExpense, useExpenseCategories, useUpdateExpense } from '@/api/expenses'
import type { ExpenseCategory, ExpenseDetail } from '@/api/expenses'
import { useLockVersion } from '@/api/lock-version'
import { useWorkspace } from '@/api/workspace'
import { todayIn } from '@/components/expenses/dates'
import { expenseErrorMessage, needsBackdateReason } from '@/components/expenses/error-message'
import { ExpenseForm } from '@/components/expenses/expense-form'
import type { ExpenseFormValues } from '@/components/expenses/expense-form'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { RecordHistory } from '@/components/record-history/record-history'
import { StaleRecordNotice } from '@/components/stale-record-notice'
import { Card, CardContent } from '@/components/ui/card'

// Correct an Expense (UI-SCREENS E-02, 07a #194): Owner only, date, category, amount, vendor
// and note, audited as `expense_edit`. A Restock Expense is read-only here — it is corrected by
// voiding its Restock, and the API answers 422 `posted_record_readonly` — so the screen shows why
// and links to M-06.
//
// The Expense's **History** lives here (07d #197, owner decision 2026-09-23, Q3): an Expense has
// no detail screen of its own, so the edit screen carries it, Owner only.
export const Route = createFileRoute('/w/$slug/expenses/$expenseId/edit')({
  component: EditExpense,
  staticData: { breadcrumb: 'expenses.form.editTitle' },
})

function EditExpense() {
  const { t } = useTranslation()
  const { slug, expenseId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const categories = useExpenseCategories(slug)
  const expense = useExpense(slug, Number(expenseId))
  const isOwner = workspace.data?.role === 'owner'
  const history = useExpenseAuditLogs(slug, Number(expenseId), { enabled: isOwner })

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/expenses" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('expenses.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('expenses.form.editTitle')}</h1>

      {workspace.isPending || categories.isPending || expense.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError ||
        !workspace.data?.adopted_on ||
        categories.isError ||
        !categories.data ||
        expense.isError ||
        !expense.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : (
        <>
          {expense.data.can.edit ? (
            <EditForm
              slug={slug}
              adoptedOn={workspace.data.adopted_on}
              timezone={workspace.data.timezone}
              categories={categories.data}
              expense={expense.data}
            />
          ) : (
            <Card>
              <CardContent className="flex flex-col items-start gap-2 py-4">
                <p role="status" className="text-sm">
                  {t('expenses.errors.restockReadonly')}
                </p>
                {expense.data.restock_id ? (
                  <Link
                    to="/w/$slug/materials/restocks/$restockId"
                    params={{ slug, restockId: String(expense.data.restock_id) }}
                    className="text-sm underline-offset-4 hover:underline"
                  >
                    {t('expenses.openRestock')}
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          )}
          {isOwner ? (
            <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} />
          ) : null}
        </>
      )}
    </section>
  )
}

function EditForm({
  slug,
  adoptedOn,
  timezone,
  categories,
  expense,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  categories: ExpenseCategory[]
  expense: ExpenseDetail
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const update = useUpdateExpense()
  const version = useLockVersion(expense.lock_version)
  // Bumped by *Muat versi terbaru*: remounts the form on the Expense as it is now saved (#142).
  const [attempt, setAttempt] = useState(0)
  const [askReason, setAskReason] = useState(false)

  // The reserved category stays out of the picker, and so does an archived one — except the
  // Expense's own, so a correction never silently moves it out of an archived category (G27).
  const selectable = categories.filter(
    (category) => !category.system && (!category.archived_at || category.id === expense.category.id),
  )

  function onSubmit(values: ExpenseFormValues) {
    const reason = values.backdateReason.trim()
    update.mutate(
      {
        params: { path: { slug, id: expense.id } },
        body: {
          expense: {
            occurred_on: values.occurredOn,
            expense_category_id: Number(values.categoryId),
            amount: Number(values.amount),
            vendor: values.vendor.trim() === '' ? null : values.vendor.trim(),
            note: values.note.trim() === '' ? null : values.note.trim(),
            lock_version: version.lockVersion,
            ...(reason === '' ? {} : { backdate_reason: reason }),
          },
        },
      },
      {
        onSuccess: () => navigate({ to: '/w/$slug/expenses', params: { slug } }),
        onSettled: (_data, error) => {
          version.settle(error)
          if (needsBackdateReason(error)) setAskReason(true)
        },
      },
    )
  }

  return (
    <>
      {version.stale ? (
        <StaleRecordNotice
          onReload={() => {
            setAttempt((count) => count + 1)
            version.reload()
            update.reset()
          }}
        />
      ) : null}
      <ExpenseForm
        key={attempt}
        slug={slug}
        adoptedOn={adoptedOn}
        today={todayIn(timezone)}
        categories={selectable}
        defaults={{
          occurredOn: expense.occurred_on,
          categoryId: String(expense.category.id),
          amount: String(expense.amount),
          vendor: expense.vendor ?? '',
          note: expense.note ?? '',
          backdateReason: '',
        }}
        pending={update.isPending}
        errorMessage={update.isError && !version.stale ? expenseErrorMessage(t, update.error) : ''}
        askReason={askReason}
        submitKey="expenses.form.save"
        onSubmit={onSubmit}
      />
    </>
  )
}
